import { Action } from 'redux';
import {
    AccountCore,
    Profile,
    AccountOverview,
    AdvancedOptions,
    InboundOptions,
    ReputationDetail,
    UsageData,
    MarketingOptions,
    NewAccountPayload,
    CrcFormPayload,
    CrcProfile,
    CrcDecision,
    AccountWarnings,
    CRCStatus,
    ProductCode,
    BrandingInfo,
    LoginPayload,
    IntercomData,
    Limits,
} from '@dev/ee-api/ee-api-models';
import { ApplicationState } from '..';

export enum AccountActionTypes {
    FETCH_START = '@@account/FETCH_START',
    FETCH_ERROR = '@@account/FETCH_ERROR',
    FETCH_SUCCESS = '@@account/FETCH_SUCCESS',
    UPDATE_MARKETING_OPTIONS_START = '@@account/UPDATE_MARKETING_OPTIONS_START',
    UPDATE_MARKETING_OPTIONS_SUCCESS = '@@account/UPDATE_MARKETING_OPTIONS_SUCCESS',
    UPDATE_START = '@@account/UPDATE_START',
    UPDATE_GLOBAL_CONTACTS = '@@account/UPDATE_GLOBAL_CONTACTS',
    UPDATE_SUCCESS = '@@account/UPDATE_SUCCESS',
    UPDATE_ERROR = '@@account/UPDATE_ERROR',
    UPLOAD_LOGO_START = '@@account/UPLOAD_LOGO_START',
    DELETE_LOGO = '@@account/DELETE_LOGO',
    UPLOAD_LOGO_FINISH = '@@account/UPLOAD_LOGO_FINISH',
    CREATE_ACCOUNT = '@@account/CREATE_ACCOUNT',
    CREATE_ACCOUNT_WITH_GOOGLE = '@@account/CREATE_ACCOUNT_WITH_GOOGLE',
    CREATE_ACCOUNT_WITH_GOOGLE_SUCCESS = '@@account/CREATE_ACCOUNT_WITH_GOOGLE_SUCCESS',
    CREATE_ACCOUNT_START = '@@account/CREATE_ACCOUNT_START',
    CREATE_ACCOUNT_SUCCESS = '@@account/CREATE_ACCOUNT_SUCCESS',
    CREATE_ACCOUNT_ERROR = '@@account/CREATE_ACCOUNT_ERROR',
    RESET_PASSWORD = '@@account/RESET_PASSWORD',
    RESET_PASSWORD_START = '@@account/RESET_PASSWORD_START',
    VALIDATE_INPUT = '@@account/VALIDATE_INPUT',
    VALIDATE_INPUT_ERROR = '@@account/VALIDATE_INPUT_ERROR',
    VALIDATE_INPUT_SUCCESS = '@@account/VALIDATE_INPUT_SUCCESS',
    RESET_PASSWORD_SUCCESS = '@@account/RESET_PASSWORD_SUCCESS',
    RESET_PASSWORD_ERROR = '@@account/RESET_PASSWORD_ERROR',
    RESET_PASSWORD_USER = '@@account/RESET_PASSWORD_USER',
    REMOVE_OPTION_FROM_UPDATE = '@@account/REMOVE_OPTION_FROM_UPDATE',
    SET_UPDATING_OPTIONS = '@@account/SET_UPDATING_OPTIONS',
    LOGIN_WITH_DISPOSABLE_TOKEN = '@@account/LOGIN_WITH_DISPOSABLE_TOKEN',
    PURCHASE_PRIVATE_IP = '@@account/PURCHASE_PRIVATE_IP',
    GET_ACCOUNTS_NAME = '@@account/GET_ACCOUNTS_NAME',
    GET_ACCOUNTS_NAME_SUCCESS = '@@account/GET_ACCOUNTS_NAME_SUCCESS',
    GET_ACCOUNTS_NAME_ERROR = '@@account/GET_ACCOUNTS_NAME_ERROR',
    UPDATE_CRC_PARAM = '@@account/UPDATE_CRC_PARAM',
    SEND_CRC = '@@account/SEND_CRC',
    CRC_SUCCESS = '@@account/CRC_SUCCESS',
    CRC_FAILED = '@@account/CRC_FAILED',
    CRC_VALIDATION = '@@account/CRC_VALIDATION',
    CRC_VALIDATION_SUCCESS = '@@account/CRC_VALIDATION_SUCCESS',
    CRC_VALIDATION_FAILURE = '@@account/CRC_VALIDATION_FAILURE',
    DELETE_ACCOUNT = '@@account/DELETE_ACCOUNT',
    GET_ACCOUNT_WARNINGS = '@@account/GET_ACCOUNT_WARNINGS',
    GET_ACCOUNT_WARNINGS_SUCCESS = '@@account/GET_ACCOUNT_WARNINGS_SUCCESS',
    UPDATE_CRC_STATUS_IN_ACCOUNT = '@@account/UPDATE_CRC_STATUS_IN_ACCOUNT',
    GET_CUSTOM_BRANDING_START = '@@account/GET_CUSTOM_BRANDING_START',
    GET_CUSTOM_BRANDING_SUCCESS = '@@account/GET_CUSTOM_BRANDING_SUCCESS',
    GET_CUSTOM_BRANDING_ERROR = '@@account/GET_CUSTOM_BRANDING_ERROR',
    PURCHASE_EXTENDED_LOGS = '@@account/PURCHASE_EXTENDED_LOGS',
    SET_VALID_SENDER_DOMAINS_ONLY = '@@account/SET_VALID_SENDER_DOMAINS_ONLY',
    UPDATE_GENERAL_INFO = '@@account/UPDATE_GENERAL_INFO',
    SET_VALID_SENDER_DOMIANS_ONLY_ERROR = '@@account/SET_VALID_SENDER_DOMIANS_ONLY_ERROR',
    RESET_ACCOUNT_EMAIL_START = '@@account/RESET_ACCOUNT_EMAIL_START',
    RESET_ACCOUNT_EMAIL_SUCCESS = '@@account/RESET_ACCOUNT_EMAIL_SUCCESS',
    RESET_ACCOUNT_EMAIL_ERROR = '@@account/RESET_ACCOUNT_EMAIL_ERROR',
    UPDATE_OVERVIEW_AI_TOOLS_TEXT_COUNT = '@@account/UPDATE_OVERVIEW_AI_TOOLS_TEXT_COUNT',

    SET_MARKETING_CONSENT_START = '@@account/SET_MARKETING_CONSENT_START',
    SET_MARKETING_CONSENT_SUCCESS = '@@account/SET_MARKETING_CONSENT_SUCCESS',
    SET_MARKETING_CONSENT_ERROR = '@@account/SET_MARKETING_CONSENT_ERROR',
}

export interface SetMarketingConsentStart extends Action {
    type: AccountActionTypes.SET_MARKETING_CONSENT_START;
    payload: boolean;
    meta: {
        thunk: boolean;
    };
}

export interface SetMarketingConsentSuccess extends Action {
    type: AccountActionTypes.SET_MARKETING_CONSENT_SUCCESS;
    meta?: Record<string, unknown>;
}

export interface SetMarketingConsentError extends Action {
    type: AccountActionTypes.SET_MARKETING_CONSENT_ERROR;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface AccountFetchStartAction extends Action {
    type: AccountActionTypes.FETCH_START;
    payload: {
        accountLoading: boolean;
    };
}
export interface AccountFetchErrorAction extends Action {
    type: AccountActionTypes.FETCH_ERROR;
    payload: {
        accountLoading: boolean;
    };
}
export interface AccountFetchSuccessAction extends Action {
    type: AccountActionTypes.FETCH_SUCCESS;
    payload: {
        account;
        accountLoading: boolean;
    };
}

export interface AccountUpdateMarketingOptionsStartAction extends Action {
    type: AccountActionTypes.UPDATE_MARKETING_OPTIONS_START;
    payload: {
        options: MarketingOptions[];
    };
}

export interface AccountUpdateMarketingOptionsSuccessAction extends Action {
    type: AccountActionTypes.UPDATE_MARKETING_OPTIONS_SUCCESS;
    payload: {
        account;
    };
}

//Update account(Advanced Settings)
export interface AccountUpdateStartAction extends Action {
    type: AccountActionTypes.UPDATE_START;
    payload: {
        option: keyof AdvancedOptions;
        value: string | boolean | number;
        accountLoading?: boolean;
    };
    meta: {
        thunk: boolean;
    };
}

export interface AccountUpdateGlobalContactsAction extends Action {
    type: AccountActionTypes.UPDATE_GLOBAL_CONTACTS;
    payload: {
        globalcontacts: boolean;
    };
    meta: {
        thunk: boolean;
    };
}

export interface AccountUpdateSuccessAction extends Action {
    type: AccountActionTypes.UPDATE_SUCCESS;
    payload: {
        account;
        accountLoading: boolean;
    };
    meta: Record<string, unknown>;
}
export interface AccountUpdateErrorAction extends Action {
    type: AccountActionTypes.UPDATE_ERROR;
    payload: Record<string, string | boolean>;
    error: boolean;
    meta: Record<string, unknown>;
}
export interface AccountUploadLogoAction extends Action {
    type: AccountActionTypes.UPLOAD_LOGO_START;
    payload: {
        file: File;
    };
}
export interface AccountDeleteLogoAction extends Action {
    type: AccountActionTypes.DELETE_LOGO;
    payload: {
        fileName: string;
    };
}
export interface AccountFinishUploadLogoAction extends Action {
    type: AccountActionTypes.UPLOAD_LOGO_FINISH;
}

export interface CreateAccountAction extends Action {
    type: AccountActionTypes.CREATE_ACCOUNT;
    payload: NewAccountPayload;
    meta: Record<string, unknown>;
}

export interface CreateAccountWithGoogleAction extends Action {
    type: AccountActionTypes.CREATE_ACCOUNT_WITH_GOOGLE;
    payload: LoginPayload;
    meta: Record<string, unknown>;
}

export interface CreateAccountWithGoogleSuccessAction extends Action {
    type: AccountActionTypes.CREATE_ACCOUNT_WITH_GOOGLE_SUCCESS;
    payload: {
        createAccountInProgress?: boolean;
    };
    meta: Record<string, unknown>;
}

export interface CreateAccountStartAction extends Action {
    type: AccountActionTypes.CREATE_ACCOUNT_START;
}

export interface CreateAccountSuccessAction extends Action {
    type: AccountActionTypes.CREATE_ACCOUNT_SUCCESS;
    payload: {
        GeneralInfo?: AccountCore;
        Profile?: Profile;
        Overview?: AccountOverview;
        AdvancedOptions?: AdvancedOptions;
        InboundOptions?: InboundOptions;
        Reputation?: ReputationDetail;
        IC?: IntercomData;
        createAccountInProgress?: boolean;
    };
    meta: Record<string, unknown>;
}

export interface CreateAccountErrorAction extends Action {
    type: AccountActionTypes.CREATE_ACCOUNT_ERROR;
    payload: Record<string, unknown>;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface ResetPasswordAction extends Action {
    type: AccountActionTypes.RESET_PASSWORD;
    payload: {
        email: string;
    };
    meta: {
        thunk: boolean;
    };
    previousMeta?: Record<string, unknown>;
}

export interface ResetPasswordStartAction extends Action {
    type: AccountActionTypes.RESET_PASSWORD_START;
}

export interface ValidateInputAction extends Action {
    type: AccountActionTypes.VALIDATE_INPUT;
    payload: {
        option: Record<keyof AdvancedOptions, string>;
        type: ValidationType;
    };
}
export type ValidationType = 'email' | 'url';
export interface ValidateInputErrorAction extends Action {
    type: AccountActionTypes.VALIDATE_INPUT_ERROR;
    payload: {
        optionName: string;
    };
}
export interface ValidateInputSuccessAction extends Action {
    type: AccountActionTypes.VALIDATE_INPUT_SUCCESS;
}

export interface ResetPasswordSuccessAction extends Action {
    type: AccountActionTypes.RESET_PASSWORD_SUCCESS;
    meta: Record<string, unknown>;
}

export interface ResetPasswordErrorAction extends Action {
    type: AccountActionTypes.RESET_PASSWORD_ERROR;
    payload: Record<string, unknown>;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface ResetPasswordUserAction extends Action {
    type: AccountActionTypes.RESET_PASSWORD_USER;
    payload: {
        email: string;
    };
    previousMeta: Record<string, unknown>;
    meta: {
        thunk: boolean;
    };
}

export interface RemoveOptionFromUpdateAction extends Action {
    type: AccountActionTypes.REMOVE_OPTION_FROM_UPDATE;
    payload: {
        option: string;
    };
}
export interface SetUpdatingOptionsAction extends Action {
    type: AccountActionTypes.SET_UPDATING_OPTIONS;
    payload: {
        options: string[];
    };
}

export interface LoginWithDisposableTokenPayload {
    productCode?: ProductCode;
    isBilling?: boolean;
    dashboardUrl?: string;
    accountDashPathName?: string;
    goToLegacyDash?: boolean;
}
export interface LoginWithDisposbaleTokenAction extends Action {
    type: AccountActionTypes.LOGIN_WITH_DISPOSABLE_TOKEN;
    payload: LoginWithDisposableTokenPayload;
}

export interface PurchasePrivateIPAction extends Action {
    type: AccountActionTypes.PURCHASE_PRIVATE_IP;
    payload: {
        region: string;
        quantity: number;
    };
}
export interface GetAccountsNameAction extends Action {
    type: AccountActionTypes.GET_ACCOUNTS_NAME;
    payload: {
        subaccountEmail: string;
        nameLoading: boolean;
    };
}
export interface GetAccountsNameSuccessAction extends Action {
    type: AccountActionTypes.GET_ACCOUNTS_NAME_SUCCESS;
    payload: {
        CrcProfile: CrcProfile;
        nameLoading: boolean;
    };
}
export interface GetAccountsNameErrorAction extends Action {
    type: AccountActionTypes.GET_ACCOUNTS_NAME_ERROR;
    payload: {
        nameLoading: boolean;
    };
}

export interface UpdateCrcFormParamAction extends Action {
    type: AccountActionTypes.UPDATE_CRC_PARAM;
    payload: {
        CRCParam: CrcFormPayload;
    };
}

export interface SendCrcFormAction extends Action {
    type: AccountActionTypes.SEND_CRC;
    meta: {
        thunk: boolean;
    };
}
export interface CrcFormSuccessAction extends Action {
    type: AccountActionTypes.CRC_SUCCESS;
    meta: Record<string, any>;
}
export interface CrcFormFailureAction extends Action {
    type: AccountActionTypes.CRC_FAILED;
    meta: Record<string, any>;
    error: boolean;
}

export interface CrcValidateAction extends Action {
    type: AccountActionTypes.CRC_VALIDATION;
    payload: {
        crcDecision: CrcDecision;
        subaccountEmail?: string;
    };
    meta: {
        thunk: boolean;
    };
}

export interface CrcValidateSuccessAction extends Action {
    type: AccountActionTypes.CRC_VALIDATION_SUCCESS;
    meta: Record<string, any>;
}

export interface CrcValidateFailureAction extends Action {
    type: AccountActionTypes.CRC_VALIDATION_FAILURE;
    meta: Record<string, any>;
    error: boolean;
}

export interface DeleteAccountAction extends Action {
    type: AccountActionTypes.DELETE_ACCOUNT;
    payload: {
        password: string;
        reason: Record<string, any>;
    };
}

export interface ResetAccountEmailAction extends Action {
    type: AccountActionTypes.RESET_ACCOUNT_EMAIL_START;
    payload: {
        verificationKey: string;
        newEmail: string;
    };
    meta: {
        thunk: boolean;
    };
}
export interface ResetAccountEmailSuccessAction extends Action {
    type: AccountActionTypes.RESET_ACCOUNT_EMAIL_SUCCESS;
    meta: Record<string, unknown>;
}
export interface ResetAccountEmailErrorAction extends Action {
    type: AccountActionTypes.RESET_ACCOUNT_EMAIL_ERROR;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface GetAccountWarningsAction extends Action {
    type: AccountActionTypes.GET_ACCOUNT_WARNINGS;
}
export interface GetAccountWarningsSuccessAction extends Action {
    type: AccountActionTypes.GET_ACCOUNT_WARNINGS_SUCCESS;
    payload: {
        warnings: AccountWarnings;
    };
}
export interface UpdateCRCStatusInAccount extends Action {
    type: AccountActionTypes.UPDATE_CRC_STATUS_IN_ACCOUNT;
    payload: {
        CrcStatus: CRCStatus;
    };
}

export interface AccountGetCustomBrandingStartAction extends Action {
    type: AccountActionTypes.GET_CUSTOM_BRANDING_START;
}

export interface AccountGetCustomBrandingSuccessAction extends Action {
    type: AccountActionTypes.GET_CUSTOM_BRANDING_SUCCESS;
    payload: { brandingInfo: BrandingInfo };
}

export interface AccountGetCustomBrandingErrorAction extends Action {
    type: AccountActionTypes.GET_CUSTOM_BRANDING_ERROR;
}
export interface PurchaseExtendedLogsAction extends Action {
    type: AccountActionTypes.PURCHASE_EXTENDED_LOGS;
}

export interface SetValidSenderDomainsAction extends Action {
    type: AccountActionTypes.SET_VALID_SENDER_DOMAINS_ONLY;
    payload: {
        ValidSenderDomainsOnly: boolean;
    };
    meta: {
        thunk: boolean;
    };
}

export interface UpdateGeneralInfoAction extends Action {
    type: AccountActionTypes.UPDATE_GENERAL_INFO;
    payload: {
        GeneralInfo: AccountCore;
    };
}
export interface UpdateAiToolsTextCountAction extends Action {
    type: AccountActionTypes.UPDATE_OVERVIEW_AI_TOOLS_TEXT_COUNT;
    payload: {
        count: number;
    };
}

export interface SetValidSenderDomainsErrorAction extends Action {
    type: AccountActionTypes.SET_VALID_SENDER_DOMIANS_ONLY_ERROR;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface AccountState {
    GeneralInfo?: AccountCore;
    Profile?: Profile;
    Overview?: AccountOverview;
    Limits?: Limits;
    AdvancedOptions?: AdvancedOptions;
    InboundOptions?: InboundOptions;
    Reputation?: ReputationDetail;
    IC?: IntercomData;
    accountLoading?: boolean;
    uploadingLogo?: boolean;
    updatingOptions?: string[];
    createAccountInProgress?: boolean;
    inputError?: boolean;
    inputErrorName?: string;
    resetPasswordSent: boolean;
    resetPasswordInProgress?: boolean;
    Usage?: UsageData[];
    CRCForm?: CrcFormPayload;
    CrcProfile?: CrcProfile;
    nameLoading?: boolean;
    subaccountEmail?: string;
    warnings?: AccountWarnings[];
    BrandingInfo?: BrandingInfo;
    isBrandingInfoLoaded?: boolean;
    marketingOptionsLoading?: boolean;
}

export const getAccount = (store: ApplicationState): AccountState => store.account;
export const getCRCParams = (store: ApplicationState): CrcFormPayload => store?.account?.CRCForm;
export const getProfile = (store: ApplicationState): Profile => store?.account?.Profile;
